import type { DiceBet } from '../types.js';

/**
 * Display-only Thai Hi-Lo multipliers, mirroring the backend's RTP math in
 * apps/game/src/originals/games/dice.ts so the board can show players the odds.
 * Single-shot money is settled server-side from the same constants — these
 * figures are for the board labels only and never drive a wager.
 */
const TARGET_RTP = 0.945;
const TOTAL = 216;

/** Number of winning outcomes in the 216-space, per non-single bet. */
const TOTAL_COUNTS: Record<number, number> = {
  4: 3, 5: 6, 6: 10, 7: 15, 8: 21, 9: 25, 10: 27, 11: 27, 12: 25, 13: 21, 14: 15, 15: 10, 16: 6, 17: 3,
};

function winningOutcomes(bet: DiceBet): number {
  if (bet === 'low' || bet === 'high') return 105;
  if (bet === 'triple-any') return 6;
  const m = /^(total|triple|pair|single)-(\d+)$/.exec(bet);
  if (!m) return 0;
  const n = Number(m[2]);
  switch (m[1]) {
    case 'total': return TOTAL_COUNTS[n] ?? 0;
    case 'triple': return 1;
    case 'pair': return 16;
    case 'single': return 91;
    default: return 0;
  }
}

/**
 * The headline multiplier for a bet. single-N pays a weighted table by match
 * count; its top tier (a triple of n) is the headline shown on the board.
 */
export function dicePayoutMultiplier(bet: DiceBet): number {
  // single-N: distribution 0->125, 1->75, 2->15, 3->1 over raw x0/x2/x3/x4,
  // scaled so EV/bet = TARGET_RTP. The board shows the best tier (a triple).
  if (/^single-\d+$/.test(bet)) {
    const dist = [{ w: 125, x: 0 }, { w: 75, x: 2 }, { w: 15, x: 3 }, { w: 1, x: 4 }];
    const evRaw = dist.reduce((s, r) => s + (r.w / TOTAL) * r.x, 0);
    const scale = TARGET_RTP / evRaw;
    return Math.floor(4 * scale * 100) / 100;
  }
  const win = winningOutcomes(bet);
  if (win <= 0) return 0;
  return Math.floor(TARGET_RTP * (TOTAL / win) * 100) / 100;
}

/** Formats the multiplier as a compact label, e.g. "1.94x", "204.12x". */
export function dicePayoutLabel(bet: DiceBet): string {
  return `${dicePayoutMultiplier(bet).toFixed(2)}x`;
}

/**
 * single-N pays by how many of the three dice match: [1, 2, 3] matches →
 * multiplier. Same dist/scale math as the headline figure above (whose value
 * equals the 3-match tier).
 */
export function diceSingleTierMultipliers(): number[] {
  const dist = [{ w: 125, x: 0 }, { w: 75, x: 2 }, { w: 15, x: 3 }, { w: 1, x: 4 }];
  const evRaw = dist.reduce((s, r) => s + (r.w / TOTAL) * r.x, 0);
  const scale = TARGET_RTP / evRaw;
  return [2, 3, 4].map((x) => Math.floor(x * scale * 100) / 100);
}
